(() => {
  const money = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' });
  const formSelector = 'form[action*="/cart/add"]';

  function panelFor(form) {
    return form?.querySelector('[data-ic-accessories]') || null;
  }

  function cardFor(node) {
    return node?.closest('[data-ic-accessory-card]') || null;
  }

  function selectedAccessories(panel) {
    return [...panel.querySelectorAll('[data-ic-accessory-checkbox]:checked:not(:disabled)')];
  }

  function boundedQuantity(input, adjustment = 0) {
    const minimum = Number(input.min || 1);
    const maximum = input.max ? Number(input.max) : Number.POSITIVE_INFINITY;
    const current = Number.parseInt(input.value, 10);
    const step = Math.max(1, Number(input.step || 1));
    const next = (Number.isFinite(current) ? current : minimum) + adjustment * step;
    const bounded = Math.max(minimum, Math.min(maximum, next));
    input.value = String(bounded);
    return bounded;
  }

  function updateCard(checkbox) {
    const card = cardFor(checkbox);
    if (!card) return;
    const selected = checkbox.checked && !checkbox.disabled;
    card.classList.toggle('is-selected', selected);
    card.querySelectorAll('[data-ic-accessory-quantity], [data-ic-accessory-decrease], [data-ic-accessory-increase]').forEach(control => {
      control.disabled = !selected;
    });
  }

  function extrasTotal(panel, selected) {
    const exVat = document.documentElement.dataset.icPriceMode === 'ex';
    return selected.reduce((total, checkbox) => {
      const card = cardFor(checkbox);
      const quantity = boundedQuantity(card.querySelector('[data-ic-accessory-quantity]'));
      const inclusiveCents = Number(card.dataset.incCents || 0);
      const taxable = card.dataset.taxable === 'true';
      const rate = Number(card.dataset.vatRate || 20);
      const divisor = exVat && taxable ? 1 + rate / 100 : 1;
      return total + (inclusiveCents / divisor) * quantity;
    }, 0);
  }

  function updatePanel(panel) {
    const selected = selectedAccessories(panel);
    const summary = panel.querySelector('[data-ic-accessory-summary]');
    const form = panel.closest(formSelector);
    const addButton = form?.querySelector('[data-action="add-to-cart"]');

    panel.querySelectorAll('[data-ic-accessory-checkbox]').forEach(updateCard);

    if (summary) {
      if (selected.length === 0) {
        summary.textContent = 'No optional extras selected.';
      } else {
        const itemCount = selected.reduce((total, checkbox) => {
          const quantity = boundedQuantity(cardFor(checkbox).querySelector('[data-ic-accessory-quantity]'));
          return total + quantity;
        }, 0);
        const taxLabel = document.documentElement.dataset.icPriceMode === 'ex' ? 'ex VAT' : 'inc VAT';
        summary.textContent = `${itemCount} optional ${itemCount === 1 ? 'extra' : 'extras'} selected · ${money.format(extrasTotal(panel, selected) / 100)} ${taxLabel}`;
      }
    }

    panel.dispatchEvent(new CustomEvent('ic:accessories-updated', { bubbles: true }));

    if (addButton && form?.dataset.icOrderReady !== 'true') {
      if (!addButton.dataset.icOriginalLabel) addButton.dataset.icOriginalLabel = addButton.textContent.trim();
      addButton.textContent = selected.length
        ? `Add to basket with ${selected.length} ${selected.length === 1 ? 'extra' : 'extras'}`
        : addButton.dataset.icOriginalLabel;
    }
  }

  function mainItemFrom(form) {
    const data = new FormData(form);
    const id = Number(data.get('id'));
    const quantity = Math.max(1, Number.parseInt(data.get('quantity') || '1', 10) || 1);
    if (!Number.isFinite(id) || id <= 0) throw new Error('Please choose an available product option.');

    const item = { id, quantity };
    const sellingPlan = data.get('selling_plan');
    if (sellingPlan) item.selling_plan = sellingPlan;

    const properties = {};
    for (const [name, value] of data.entries()) {
      const match = name.match(/^properties\[(.+)]$/);
      if (match && value !== '') properties[match[1]] = value;
    }
    if (Object.keys(properties).length) item.properties = properties;
    return item;
  }

  function accessoryItems(panel) {
    return selectedAccessories(panel).map(checkbox => {
      const card = cardFor(checkbox);
      return {
        id: Number(checkbox.dataset.variantId),
        quantity: boundedQuantity(card.querySelector('[data-ic-accessory-quantity]'))
      };
    }).filter(item => Number.isFinite(item.id) && item.id > 0 && item.quantity > 0);
  }

  function clearStatus(form) {
    form.parentElement?.querySelector('.ic-accessories-status')?.remove();
  }

  function showStatus(form, message, type) {
    clearStatus(form);
    const status = document.createElement('div');
    status.className = `ic-accessories-status alert ${type === 'error' ? 'alert--error' : 'alert--success'}`;
    status.setAttribute('role', type === 'error' ? 'alert' : 'status');
    status.textContent = message;
    form.insertAdjacentElement('afterend', status);
  }

  async function addWithAccessories(form, button) {
    const panel = panelFor(form);
    if (!panel || panel.dataset.icBusy === 'true') return;
    const extras = accessoryItems(panel);
    if (!extras.length) return;

    clearStatus(form);
    panel.dataset.icBusy = 'true';
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    document.dispatchEvent(new CustomEvent('theme:loading:start'));

    let redirecting = false;
    try {
      const items = [mainItemFrom(form), ...extras];
      const response = await fetch(`${window.routes?.cartAddUrl || '/cart/add'}.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ items })
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.description || result.message || 'We could not add every item. Please check availability and try again.');

      panel.querySelectorAll('[data-ic-accessory-checkbox]:checked').forEach(checkbox => {
        checkbox.checked = false;
        const quantity = cardFor(checkbox)?.querySelector('[data-ic-accessory-quantity]');
        if (quantity) quantity.value = '1';
      });
      updatePanel(panel);

      const quantityAdded = items.reduce((total, item) => total + item.quantity, 0);
      if (window.theme?.cartType === 'page') {
        redirecting = true;
        window.location.assign(window.routes?.cartUrl || '/cart');
        return;
      }

      form.dispatchEvent(new CustomEvent('product:added', {
        bubbles: true,
        detail: { variant: null, quantity: quantityAdded, button }
      }));
    } catch (error) {
      showStatus(form, error.message || 'We could not add every item. Please try again.', 'error');
      document.dispatchEvent(new CustomEvent('theme:loading:end'));
    } finally {
      if (!redirecting) {
        panel.dataset.icBusy = 'false';
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
    }
  }

  document.addEventListener('change', event => {
    const checkbox = event.target.closest?.('[data-ic-accessory-checkbox]');
    if (checkbox) {
      updatePanel(checkbox.closest('[data-ic-accessories]'));
      return;
    }
    const quantity = event.target.closest?.('[data-ic-accessory-quantity]');
    if (quantity) {
      boundedQuantity(quantity);
      updatePanel(quantity.closest('[data-ic-accessories]'));
    }
  });

  document.addEventListener('click', event => {
    const decrease = event.target.closest?.('[data-ic-accessory-decrease]');
    const increase = event.target.closest?.('[data-ic-accessory-increase]');
    if (decrease || increase) {
      event.preventDefault();
      const card = cardFor(decrease || increase);
      boundedQuantity(card.querySelector('[data-ic-accessory-quantity]'), decrease ? -1 : 1);
      updatePanel(card.closest('[data-ic-accessories]'));
      return;
    }

    const button = event.target.closest?.('[data-action="add-to-cart"]');
    if (!button) return;
    const form = button.closest(formSelector);
    if (form?.dataset.icOrderReady === 'true') return;
    const panel = panelFor(form);
    if (!panel || selectedAccessories(panel).length === 0) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    addWithAccessories(form, button);
  }, true);

  document.addEventListener('submit', event => {
    const form = event.target.closest?.(formSelector);
    if (form?.dataset.icOrderReady === 'true') return;
    const panel = panelFor(form);
    if (!panel || selectedAccessories(panel).length === 0) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    const button = form.querySelector('[data-action="add-to-cart"]');
    if (button) addWithAccessories(form, button);
  }, true);

  document.addEventListener('variant:changed', event => {
    const form = event.target.querySelector?.(formSelector) || event.target.closest?.(formSelector);
    const panel = panelFor(form);
    if (panel) window.setTimeout(() => updatePanel(panel), 0);
  });

  document.addEventListener('click', event => {
    if (!event.target.closest?.('[data-ic-vat-toggle]')) return;
    window.setTimeout(() => document.querySelectorAll('[data-ic-accessories]').forEach(updatePanel), 0);
  });

  function initialise(scope = document) {
    scope.querySelectorAll('[data-ic-accessories]').forEach(updatePanel);
  }

  initialise();
  document.addEventListener('shopify:section:load', event => initialise(event.target));
})();
